import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { EMPTY, of, Subject, Subscription, throwError } from 'rxjs';
import { catchError, map, mapTo, pluck, switchMap, takeUntil, tap } from 'rxjs/operators';
import { XeroService } from '../xero.service';

@Component({
  selector: 'app-callback',
  templateUrl: './callback.component.html',
  styleUrls: ['./callback.component.scss']
})
export class CallbackComponent implements OnInit, OnDestroy {
  private _componentState: 'loading' | 'authenticating' | 'authenticated' | 'error' | 'nocode';

  private navigatePage$: Subscription;

  /**
   * Indicator used to unsubscribe to everything when the component gets destroyed.
   */
  private isDestroyed$: Subject<boolean>;

  public constructor(
    private xero  : XeroService,
    private route : ActivatedRoute,
    private router: Router,
    private toast : ToastrService
  ) { }

  public get componentState() {
    return this._componentState;
  }

  public get integrationInfo$() {
    return this.xero.integrationInfo$;
  }

  public ngOnInit() {
    this.isDestroyed$ = new Subject();
    this._componentState = 'loading';

    // redirect to Xero settings when a positive xero connection is detected
    this.integrationInfo$
    .pipe(takeUntil(this.isDestroyed$))
    .subscribe((info) => {
      if(info.connection.is_connected && info.connection.expires_at > Date.now()) {
        this.router.navigateByUrl(info.settings, { replaceUrl: true });
      }
    });

    this.navigatePage$ = this.route.queryParams
      .pipe(
        pluck('code'),
        switchMap((authCode) => {
          if(authCode) {
            this._componentState = 'authenticating';
            return this.xero
              .relayCode(authCode)
              .pipe(
                tap(() => this._componentState = 'authenticated'),
                map(() => this.componentState)
              );
          }
          else {
            this._componentState = 'nocode';
            return of(this.componentState);
          }
        }),
        catchError(() => {
          this._componentState = 'error';
          return of(this.componentState);
        }),
        takeUntil(this.isDestroyed$)
      )
      .subscribe((state) => {
        if(window.opener) {
          window.opener.postMessage({ connected: state === 'authenticated' }, window.location.origin);
          window.close();
          return;
        }
        
        switch(state) {
          // navigate to the Xero setup page when authenticated successfully
          case 'authenticated':      
          this.router.navigate([ 'setup' ], { relativeTo: this.route.parent });
            break;

          // display warning message
          case 'nocode':
            this.toast.warning(
              'You can try to reconnect if you want.',
              "It seems you skipped some steps trying to connect to Xero"
            );
            break;

          // display error message
          case 'error':
            this.toast.error(
              'You can try reconnecting again to see if it helps.',
              'We could not connect you to Xero for some reason'
            );
            break;
        }
      });
  }

  public ngOnDestroy() {
    this.navigatePage$.unsubscribe();
    this.isDestroyed$.next(true);
    this.isDestroyed$.unsubscribe();
  }
}
